/**
 * The windowing arithmetic, as pure functions.
 *
 * Separated from the DOM on purpose. The performance claim this component has
 * to make — tens of thousands of rows without jank — is a claim about
 * COMPLEXITY, not about a device: per scroll frame the work must be
 * proportional to the number of visible rows and to nothing else. That is a
 * property of these four functions, and it is testable without a browser.
 *
 * Fixed row height, deliberately. Variable-height windowing needs a measured
 * offset index, and maintaining one over 40,000 rows costs either a full
 * measurement pass at mount (which is the thing being avoided) or an estimate
 * that makes the scrollbar lie and the scroll position drift under the thumb.
 * Subtitle cues are a domain where uniform height is honest: the resegmenter
 * targets a maximum of two lines per cue, so a two-line row clips almost
 * nothing, and what it does clip is recoverable by opening the cue.
 *
 * `content-visibility: auto` was the alternative. It skips RENDERING work for
 * off-screen elements but not DOM construction, style resolution at insert, or
 * memory — 40,000 rows is roughly 160,000 elements, which is a mid-range phone
 * out of memory before the first paint. It is the right tool at a few hundred
 * rows and the wrong one here.
 */

export interface Range {
  /** First row index to render, inclusive. */
  readonly start: number;
  /** One past the last row index to render. */
  readonly end: number;
  /** Pixel offset the rendered window must be translated by. */
  readonly offset: number;
}

/**
 * Which rows to render for a given scroll position.
 *
 * `overscan` rows are rendered above and below the viewport so a fast flick
 * does not expose blank space between frames.
 */
export function windowRange(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  count: number,
  overscan = 6,
): Range {
  if (count <= 0 || rowHeight <= 0 || viewportHeight <= 0) {
    return { start: 0, end: 0, offset: 0 };
  }
  const top = Math.max(0, scrollTop);
  const firstVisible = Math.floor(top / rowHeight);
  const visibleCount = Math.ceil(viewportHeight / rowHeight) + 1;
  const start = Math.max(0, firstVisible - overscan);
  const end = Math.min(count, firstVisible + visibleCount + overscan);
  return { start, end, offset: start * rowHeight };
}

/** Total scrollable height. This is what keeps the native scrollbar honest. */
export function sizerHeight(count: number, rowHeight: number): number {
  return Math.max(0, count) * Math.max(0, rowHeight);
}

/** How many row elements the pool needs for a viewport. Bounded, not O(count). */
export function poolSize(viewportHeight: number, rowHeight: number, overscan = 6): number {
  if (rowHeight <= 0) return 0;
  return Math.ceil(viewportHeight / rowHeight) + 1 + overscan * 2;
}

/**
 * Which pooled element renders a given row.
 *
 * Modulo, so a scroll of one row moves exactly one element. The obvious
 * alternative — keep the pool in document order and translate the whole window
 * — is simpler and rewrites every visible row on every frame that changes the
 * range at all: a full 40,000-row scroll costs ~54,000 cell writes instead of
 * ~40,000, and every one of them lands inside a scroll frame. With modulo
 * assignment each element keeps its content until its index actually changes,
 * so the per-frame work is proportional to how far the list scrolled rather
 * than to how much of it is visible.
 *
 * The cost is that rows are positioned individually instead of by one
 * transform on a container, which is why the renderer places each row rather
 * than the window.
 */
export function slotFor(index: number, poolLength: number): number {
  return poolLength <= 0 ? 0 : ((index % poolLength) + poolLength) % poolLength;
}

/** True when the new range needs no DOM work at all. */
export function sameRange(a: Range, b: Range): boolean {
  return a.start === b.start && a.end === b.end;
}
